import React from 'react';
import { cn } from '../../lib/utils';
import { motion } from 'framer-motion';

const buttonVariants = {
  primary: 'bg-orange-600 text-white hover:bg-orange-700 shadow-lg hover:shadow-xl',
  secondary: 'bg-white dark:bg-gray-800 text-orange-600 dark:text-orange-400 border-2 border-orange-600 dark:border-orange-400 hover:bg-orange-50 dark:hover:bg-gray-700',
  outline: 'bg-transparent border-2 border-current hover:bg-white/10',
  ghost: 'hover:bg-gray-100 dark:hover:bg-gray-800',
  success: 'bg-success-600 text-white hover:bg-success-700 shadow-lg',
  danger: 'bg-error-600 text-white hover:bg-error-700 shadow-lg',
};

const buttonSizes = {
  sm: 'px-4 py-2 text-sm',
  md: 'px-6 py-3 text-base',
  lg: 'px-8 py-4 text-lg',
  xl: 'px-10 py-5 text-xl',
};

export const Button = React.forwardRef(
  ({
    className,
    variant = 'primary',
    size = 'md',
    children,
    icon,
    iconPosition = 'left',
    animate = true,
    ...props
  }, ref) => {
    const buttonContent = (
      <>
        {icon && iconPosition === 'left' && <span className="mr-2">{icon}</span>}
        {children}
        {icon && iconPosition === 'right' && <span className="ml-2">{icon}</span>}
      </>
    );

    const baseClasses = cn(
      'inline-flex items-center justify-center rounded-xl font-semibold transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2',
      buttonVariants[variant],
      buttonSizes[size],
      className
    );

    if (animate) {
      return (
        <motion.button
          ref={ref}
          className={baseClasses}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          {...props}
        >
          {buttonContent}
        </motion.button>
      );
    }

    return (
      <button ref={ref} className={baseClasses} {...props}>
        {buttonContent}
      </button>
    );
  }
);

Button.displayName = 'Button';

export default Button;
